import { yupResolver } from "@hookform/resolvers/yup"
import { useState } from "react"
import * as yup from "yup"

import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { UsersData, useCustomerPageContext } from "@contexts/customer"
import { updateCustomerProducts } from "@domain/customer/infos"
import useControlledForm from "@hooks/use_controlled_form"
import { Stack } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"

export type SetAccountBalanceForm = {
  amount: number
}

export default function SetAccountBalance({
  isOpen,
  setClose
}: {
  isOpen: boolean
  selectedRow?: UsersData
  setClose: () => void
}) {
  const { translate, currentCustomer: customer } = useCustomerPageContext()
  const queryClient = useQueryClient()

  const schema = yup
    .object({
      amount: yup
        .number()
        .required(translate("amount_field_is_required"))
        .min(0)
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty }
  } = useControlledForm<SetAccountBalanceForm>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      amount: Number(customer?.package?.accountBalance?.toFixed(2)) || 0
    }
  })

  const [loading, setLoading] = useState(false)

  function handleClose() {
    if (loading) {
      return
    }

    setClose()
    reset({
      amount: Number(customer?.package?.accountBalance?.toFixed(2)) || 0
    })
  }

  async function onSubmit(data: SetAccountBalanceForm) {
    if (!customer?.package?._id) {
      return
    }

    setLoading(true)
    const { isSuccess, errorMessage } = await updateCustomerProducts({
      _id: customer.package._id,
      directAccountBalance: data.amount
    })

    if (!isSuccess && errorMessage) {
      showErrorToast(errorMessage)
      setLoading(false)
      return
    }

    setLoading(false)
    queryClient.invalidateQueries({ queryKey: ["customer-data-table"] })
    handleClose()
  }

  return (
    <DialogProvider
      title={translate("update_account_balance")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("save"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading,
        disabled: !isValid || !isDirty
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <TextInput<SetAccountBalanceForm>
          type="number"
          label={translate("amount")}
          disabled={loading}
          name="amount"
          placeholder="0"
          control={control}
        />
      </Stack>
    </DialogProvider>
  )
}
